import { z } from 'zod';

export const CreateBookingInputSchema = z.object({
  gymId: z.string().uuid(),
  slotStartTime: z.string().datetime(),
  offerCode: z.string().trim().min(3).max(32).optional(),
  useCredit: z.boolean().optional(),
  includePartner: z.boolean().optional().default(false),
  notes: z.string().max(500).optional(),
});

export const CreateBookingBatchInputSchema = z.object({
  gymId: z.string().uuid(),
  slotStartTimes: z.array(z.string().datetime()).min(1).max(24),
  offerCode: z.string().trim().min(3).max(32).optional(),
  useCredit: z.boolean().optional(),
  includePartner: z.boolean().optional().default(false),
  notes: z.string().max(500).optional(),
});

export const CreateBookingIntentInputSchema = CreateBookingInputSchema.extend({
  slotStartTimes: z.array(z.string().datetime()).min(1).max(24).optional(),
});

export const CancelBookingInputSchema = z.object({
  reason: z.string().max(500).optional(),
});

export const AdminCancelBookingInputSchema = CancelBookingInputSchema.extend({
  refundMode: z.enum(['CREDIT', 'CASH', 'NONE']).default('NONE'),
});

export const CreateManualBookingInputSchema = z.object({
  gymId: z.string().uuid(),
  slotStartTime: z.string().datetime(),
  email: z.string().email().max(320),
  firstName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  phone: z.string().trim().max(40).optional(),
  priceInSmallestUnit: z.coerce.number().int().min(0).optional(),
  currency: z.string().trim().length(3).default('CZK'),
  paymentStatus: z.enum(['PENDING', 'PAID']).default('PAID'),
  notes: z.string().max(500).optional(),
});

export type CreateBookingInput = z.infer<typeof CreateBookingInputSchema>;
export type CreateBookingBatchInput = z.infer<typeof CreateBookingBatchInputSchema>;
export type CreateBookingIntentInput = z.infer<typeof CreateBookingIntentInputSchema>;
export type CancelBookingInput = z.infer<typeof CancelBookingInputSchema>;
export type AdminCancelBookingInput = z.infer<typeof AdminCancelBookingInputSchema>;
export type CreateManualBookingInput = z.infer<typeof CreateManualBookingInputSchema>;

export const ReservationSortSchema = z.enum([
  'tenant',
  'customer',
  'createdAt',
  'creator',
  'phone',
  'gym',
  'scheduled',
  'type',
  'price',
  'payment',
  'code',
  'status',
]);
export type ReservationSort = z.infer<typeof ReservationSortSchema>;

export const ListOwnerBookingsQuerySchema = z.object({
  sortBy: ReservationSortSchema.optional(),
  sortDirection: z.enum(['asc', 'desc']).optional(),
  status: z
    .enum(['PENDING', 'PAID', 'ACTIVE', 'COMPLETED', 'NO_SHOW', 'CANCELLED', 'REFUNDED'])
    .optional(),
  gymId: z.string().uuid().optional(),
  accountId: z.string().uuid().optional(),
  q: z.string().max(200).optional(),
  range: z.enum(['today', 'week', 'lastWeek', 'month', 'custom']).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  page: z.coerce.number().int().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export type ListOwnerBookingsQuery = z.infer<typeof ListOwnerBookingsQuerySchema>;

export const UpdateBookingStatusInputSchema = z.object({
  status: z.enum(['PENDING', 'PAID', 'ACTIVE', 'COMPLETED', 'NO_SHOW', 'CANCELLED', 'REFUNDED']),
});
